import {
  IonCheckbox,
  IonCol,
  IonIcon,
  IonItem,
  useIonModal
} from "@ionic/react";
import { barChart } from "ionicons/icons";
import TradingViewChart from "./TradingViewChart";
import { useBotsStore } from "../stores/bots.store";
import { addBotAction, createActionByType } from "../lib/firebase";
import { useAuthStore } from "../stores/auth.store";
import getCoinImage from "../utils/getCoinImage";
import { useIndicatorsStore } from "../stores/indicators.store";
import { useEffect } from "react";
import ManualBuy from "./ManualBuy";
import RSIReading from "./RSIReading";

interface CoinListItemProps {
  coin: any;
  botId?: string;
  index: number;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: (coin: any) => void;
  options?: any;
}

function formatNumber(number: any) {
  if (number === undefined) {
    return "-";
  }
  // Check if the number has a decimal point
  const decimalIndex = number.toString().indexOf(".");
  if (decimalIndex !== -1) {
    // Remove trailing zeros after the decimal point
    number = number.toFixed(8).replace(/0+$/, "");

    // Remove the decimal point if there are no digits after it
    if (number[number.length - 1] === ".") {
      number = number.slice(0, -1);
    }
  }

  // Round to 2 decimals if there is a number before the decimal point
  if (decimalIndex !== 0) {
    number = Number(number).toFixed(2);
  }

  // Add comma separators for thousands and millions
  const parts = number.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Join the parts back together with a decimal point
  const formattedNumber = parts.join(".");

  return formattedNumber;
}

const CoinListItem: React.FC<CoinListItemProps> = ({
  coin,
  botId,
  index,
  selectionMode,
  selected,
  onSelect,
  options
}) => {
  const uid = useAuthStore(state => state.user?.uid);
  const bot = useBotsStore(state => state.bots.find(bot => bot.id === botId));
  const [presentChart, dismissChart] = useIonModal(TradingViewChart, {
    onDismiss: () => dismissChart(),
    symbol: coin.symbol,
    interval: bot?.tradeParameters?.tickerInterval || "15m"
  });
  const coinId = coin.symbol.replace("USDT", "");

  const currentUSDTBalance = parseFloat(
    bot?.data?.portfolio?.coinBalances?.find(
      (balance: any) => balance.asset === "USDT"
    )?.free || 0
  );

  useEffect(() => {
    if (options && bot?.tradeParameters?.tickerInterval) {
      useIndicatorsStore
        .getState()
        .fetchIndicatorReadingsAndProcessData(
          coin.symbol,
          bot?.tradeParameters?.tickerInterval
        );
    }
  }, [options, bot?.tradeParameters?.tickerInterval, coin.symbol]);

  const [presentConfirm, dismissConfirm] = useIonModal(ManualBuy, {
    onDismiss: (confirm: boolean, buyAmount: number) => {
      dismissConfirm();
      if (confirm && uid && botId) {
        const action = createActionByType("manualBuy", uid, {
          botId,
          coinPair: coin.symbol,
          asset: bot?.quoteAsset,
          assetQuantity: buyAmount || bot?.data?.portfolio?.buyAmount || 30
        });
        addBotAction(botId, action)
          .then(() => console.log("Manual buy signal sent."))
          .catch((error: any) =>
            console.error("Data could not be saved." + error)
          );
      }
    },
    interval: bot?.tradeParameters?.tickerInterval || "15m",
    coinId,
    coin,
    availableQuoteBalance: currentUSDTBalance
  });
  // const changePercent = Math.round(Number(coin.changePercent));
  const changePercent = Number(coin.changePercent).toFixed(1);
  const canBuy =
    currentUSDTBalance > 10 &&
    bot?.active &&
    coin.symbol !== "USDTUSDT" &&
    bot?.user === uid;

  return (
    <IonItem
      button={options && (canBuy || selectionMode)}
      detail={false}
      onClick={() => {
        if (!options) return;
        if (canBuy) {
          presentConfirm();
        } else if (selectionMode && onSelect) {
          onSelect(coin);
        }
      }}
    >
      <IonCol
        size="3"
        className="text-center text-slate-200 text-sm flex flex-row items-center relative"
      >
        {/* <span className="absolute top-0 text-slate-400 left-0">
          {coin.rank}
        </span> */}
        <div className="absolute text-xs text-gray-500 -mt-5 -ml-1">
          {index}
        </div>
        <img
          src={getCoinImage(coinId)}
          alt={coin}
          className="mx-2 w-[32px] h-[32px]"
        />
        {coinId}
      </IonCol>
      {botId && !options && (
        <>
          <IonCol size="4" className="text-center text-white text-sm">
            {formatNumber(coin?.balance)}
          </IonCol>
          <IonCol size="3" className="text-center text-white text-sm">
            {formatNumber(coin?.quoteValue)}
          </IonCol>
        </>
      )}
      {botId && options && (
        <>
          <IonCol size="3" className="text-center text-white text-sm">
            {formatNumber(coin?.price)}
          </IonCol>
          <IonCol
            size="3"
            className=" text-white text-sm flex items-center justify-center"
          >
            <RSIReading
              symbol={coin.symbol}
              interval={bot?.tradeParameters?.tickerInterval || "15m"}
            />
          </IonCol>
        </>
      )}
      {!botId && (
        <IonCol size="6" className="text-center text-white text-sm">
          {coin?.price}
        </IonCol>
      )}
      <IonCol
        size="2"
        className={`text-center text-sm font-bold ${
          coin?.change > 0 ? "text-green-500" : "text-red-500"
        }`}
      >
        {changePercent}%
      </IonCol>
      {options && !selectionMode && (
        <IonCol
          size="1"
          onClick={event => {
            event.stopPropagation();
            coinId !== "USDT" && presentChart();
          }}
          className="flex justify-center items-center text-slate-200 text-sm bg-white rounded-lg button"
        >
          <IonIcon className="text-black text-sm" icon={barChart} />
        </IonCol>
      )}
      {selectionMode && (
        <IonCol size="1">
          <IonCheckbox
            mode="md"
            checked={selected}
            onIonChange={e => {
              e.stopPropagation();
              onSelect && onSelect(coin);
            }}
          />
        </IonCol>
      )}
    </IonItem>
  );
};

export default CoinListItem;
